<script>
import { mapActions, mapState } from 'pinia'
import AppSideMenu from '@/components/layout/AppSideMenu.vue'
import { useUiStore } from '@/stores/ui'

function readVisibleViewportHeightPx() {
  if (typeof window === 'undefined') return null
  const vv = window.visualViewport
  if (vv && Number.isFinite(vv.height)) return Math.max(0, Math.round(vv.height))
  return Math.max(0, Math.round(window.innerHeight))
}

/**
 * 모바일 브라우저: 100vh는 주소창이 펼쳐진 상태와 맞지 않아 셸이 실제보다 커지고,
 * 하단 고정 탭에 콘텐츠가 가려질 수 있음. visualViewport.height로 맞춘 뒤
 * 주소창 표시/숨김·회전 시에도 동기화한다.
 */
export default {
  name: 'App',
  components: {
    AppSideMenu
  },
  data() {
    return {
      transitionName: 'slide-left',
      viewportHeightPx: readVisibleViewportHeightPx()
    }
  },
  computed: {
    ...mapState(useUiStore, ['isSideMenuOpen']),
    appShellStyle() {
      const h = this.viewportHeightPx
      if (h == null || h <= 0) {
        return { height: '100dvh', maxHeight: '100dvh' }
      }
      return {
        height: `${h}px`,
        maxHeight: `${h}px`
      }
    }
  },
  watch: {
    '$route'(to, from) {
      const toDepth = to.path.split('/').length
      const fromDepth = from.path.split('/').length
      this.transitionName = toDepth >= fromDepth ? 'slide-left' : 'slide-right'
      this.closeSideMenu()
      if (!from || to.fullPath !== from.fullPath) {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.scrollMainToTop())
        })
      }
    }
  },
  mounted() {
    this.syncViewportHeight()
    this.$nextTick(() => {
      requestAnimationFrame(() => this.syncViewportHeight())
    })
    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', this.syncViewportHeight)
      vv.addEventListener('scroll', this.syncViewportHeight)
    }
    window.addEventListener('resize', this.syncViewportHeight)
  },
  beforeUnmount() {
    const vv = window.visualViewport
    if (vv) {
      vv.removeEventListener('resize', this.syncViewportHeight)
      vv.removeEventListener('scroll', this.syncViewportHeight)
    }
    window.removeEventListener('resize', this.syncViewportHeight)
  },
  methods: {
    ...mapActions(useUiStore, ['closeSideMenu']),
    syncViewportHeight() {
      const next = readVisibleViewportHeightPx()
      if (next != null && next !== this.viewportHeightPx) {
        this.viewportHeightPx = next
      }
    },
    /** 라우트 이동 시 페이지 내부 스크롤 영역·문서 스크롤을 맨 위로 */
    scrollMainToTop() {
      const el = this.$refs.appScroll
      if (el) {
        el.scrollTop = 0
        el.scrollLeft = 0
        el.querySelectorAll('.app-page-scroll, .history-list-scroll, .app-view-fill, .login-figma').forEach((node) => {
          node.scrollTop = 0
          node.scrollLeft = 0
        })
      }
      window.scrollTo(0, 0)
      if (document.documentElement) {
        document.documentElement.scrollTop = 0
        document.documentElement.scrollLeft = 0
      }
      if (document.body) {
        document.body.scrollTop = 0
        document.body.scrollLeft = 0
      }
    }
  }
}
</script>

<template>
  <!-- 세로 스크롤은 라우트(페이지)별 스크롤 영역에서 처리. 셸은 visualViewport 높이에 맞춤. -->
  <div
    class="app-shell relative mx-auto flex min-h-0 w-full max-w-[430px] flex-col overflow-x-hidden bg-dark-bg font-pretendard"
    :style="appShellStyle"
  >
    <div
      ref="appScroll"
      class="app-scroll no-scrollbar relative flex min-h-0 flex-1 flex-col overflow-y-hidden overflow-x-hidden"
    >
      <router-view v-slot="{ Component, route }">
        <!-- overflow-y는 페이지(.app-view-fill / .app-page-scroll)에서 처리. overflow-hidden이면 세로 스크롤이 막힘 -->
        <component :is="Component" :key="route.fullPath" class="flex min-h-0 flex-1 flex-col w-full overflow-x-hidden" />
      </router-view>
    </div>
    <AppSideMenu :open="isSideMenuOpen" @close="closeSideMenu" />
  </div>
</template>
